/* BABYGO blog index (25 Sep 2026). Loaded by sections/main-blog.liquid.

   Show more: in the HTML it is a real link to the next native page of this blog or category, so it works without
   this script. Here it becomes a button that fetches that page's copy of the section (Section Rendering API,
   ?section_id=) and appends its tiles, skipping any post already on screen: a post published between two loads
   shifts every page by one, and would otherwise appear twice. When the last page is in, the button goes. If a
   request fails, the browser follows the link instead, so the reader still gets the next page.

   Coming back: the page can't use the browser's back/forward cache (third-party scripts), so Back from a post
   would otherwise show page one only, at a scroll position that no longer matches. The number of pages shown and
   the scroll position are kept in history.state (the URL doesn't change), and on Back or reload the same pages are
   loaded again before the scroll position is put back.

   Category row: on phones the row scrolls sideways; the current category is brought into view on load. */
(() => {
  'use strict';

  const STATE_KEY = 'bgbIndex';
  const MAX_RESTORE = 10;

  const revealCurrentCategory = (root) => {
    root.querySelectorAll('[data-bgb-cats]').forEach((row) => {
      const current = row.querySelector('[aria-current]');
      if (!current || row.scrollWidth <= row.clientWidth) return;
      const rowBox = row.getBoundingClientRect();
      const linkBox = current.getBoundingClientRect();
      if (linkBox.left >= rowBox.left && linkBox.right <= rowBox.right) return;
      row.scrollLeft += linkBox.left - rowBox.left - (rowBox.width - linkBox.width) / 2;
    });
  };

  const readState = () => {
    try {
      return (window.history.state && window.history.state[STATE_KEY]) || null;
    } catch (error) {
      return null;
    }
  };

  const saveState = (sectionId, patch) => {
    try {
      const state = Object.assign({}, window.history.state);
      state[STATE_KEY] = Object.assign({}, state[STATE_KEY], patch, { id: sectionId });
      window.history.replaceState(state, '');
    } catch (error) {
      /* history unavailable: Show more still works, Back just starts from page one */
    }
  };

  const fetchPage = async (href, sectionId) => {
    const url = new URL(href, window.location.href);
    url.searchParams.set('section_id', sectionId);
    const response = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Blog page request failed: ' + response.status);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const grid = doc.querySelector('[data-bgb-grid]');
    if (!grid) throw new Error('Blog page response had no posts');
    const next = doc.querySelector('[data-bgb-more]');
    return { tiles: Array.from(grid.querySelectorAll('[data-bgb-article]')), next: next ? next.getAttribute('href') : null };
  };

  const initList = (list) => {
    if (list.dataset.bgbReady) return;
    const grid = list.querySelector('[data-bgb-grid]');
    const more = list.querySelector('[data-bgb-more]');
    const status = list.querySelector('[data-bgb-status]');
    const sectionId = list.dataset.sectionId;
    if (!grid || !more || !sectionId) return;
    list.dataset.bgbReady = 'true';
    const total = parseInt(list.dataset.total, 10) || 0;
    let busy = false;
    let pagesAdded = 0;

    /* One Show more step: fetch the next page (and the one after, if every post on it is already shown), append
       the new tiles and move the link on. Returns the first new tile, or null. Throws if a request fails. */
    const loadNext = async () => {
      const onPage = new Set(Array.from(grid.querySelectorAll('[data-bgb-article]'), (tile) => tile.dataset.bgbArticle));
      let fresh = [];
      let next = more.getAttribute('href');
      while (next && !fresh.length) {
        const page = await fetchPage(next, sectionId);
        fresh = page.tiles.filter((tile) => !onPage.has(tile.dataset.bgbArticle));
        next = page.next;
      }
      fresh.forEach((tile) => grid.appendChild(document.importNode(tile, true)));
      pagesAdded += 1;
      const shown = grid.querySelectorAll('[data-bgb-article]').length;
      if (next) {
        more.setAttribute('href', next);
        if (status) status.textContent = total ? 'Showing ' + shown + ' of ' + total + ' posts' : 'Showing ' + shown + ' posts';
      } else {
        more.remove();
        if (status) status.textContent = 'Showing all ' + shown + ' posts';
      }
      return fresh.length ? grid.querySelector('[data-bgb-article="' + fresh[0].dataset.bgbArticle + '"] a') : null;
    };

    const setBusy = (on) => {
      busy = on;
      if (on) {
        more.setAttribute('aria-disabled', 'true');
        grid.setAttribute('aria-busy', 'true');
      } else {
        more.removeAttribute('aria-disabled');
        grid.removeAttribute('aria-busy');
      }
    };

    more.setAttribute('role', 'button');
    more.addEventListener('keydown', (event) => {
      if (event.key === ' ') {
        event.preventDefault();
        more.click();
      }
    });

    more.addEventListener('click', async (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (busy) return;
      const href = more.getAttribute('href');
      setBusy(true);
      try {
        const firstNew = await loadNext();
        if (firstNew) firstNew.focus({ preventScroll: true });
        saveState(sectionId, { pages: pagesAdded, y: Math.round(window.scrollY) });
      } catch (error) {
        window.location.assign(href);
      } finally {
        setBusy(false);
      }
    });

    /* Remember where the reader was when they open a post, so Back can return them there. */
    grid.addEventListener('click', (event) => {
      if (pagesAdded && event.target.closest('a[href]')) saveState(sectionId, { pages: pagesAdded, y: Math.round(window.scrollY) });
    });

    const saved = readState();
    const navigation = window.performance && performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    const returning = navigation && (navigation.type === 'back_forward' || navigation.type === 'reload');
    if (saved && saved.id === sectionId && saved.pages > 0 && returning) {
      (async () => {
        setBusy(true);
        try {
          for (let i = 0; i < Math.min(saved.pages, MAX_RESTORE) && more.isConnected; i += 1) await loadNext();
        } catch (error) {
          /* keep what arrived; Show more carries on from there */
        } finally {
          setBusy(false);
        }
        if (typeof saved.y === 'number') window.scrollTo(0, saved.y);
      })();
    }
  };

  const init = (root) => {
    root.querySelectorAll('[data-bgb-list]').forEach(initList);
    revealCurrentCategory(root);
  };

  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
